/**
 * SocialPulseNetwork — /network directory.
 *
 * A single-glance map of every sister site: name, purpose, status
 * (Live / Pilot / Planned), scope (India / Kerala / USA / Global etc.)
 * and canonical URL. Each row links out to the property's canonical
 * home so visitors can jump straight from the ecosystem overview into
 * any specialist site.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, Search, Filter, ExternalLink, Activity, Landmark,
  Newspaper, Camera, Globe2, Hotel, Home as HomeIcon, HeartPulse,
  Users, Code2, MapPin,
} from "lucide-react";
import SocialPulseHeader from "@/components/socialpulse/SocialPulseHeader";
import SocialPulseFooter from "@/components/socialpulse/SocialPulseFooter";

const STATUSES = {
  live:    { label: "Live",    dot: "bg-emerald-500", pill: "bg-emerald-50 text-emerald-700 ring-emerald-200" },
  pilot:   { label: "Pilot",   dot: "bg-amber-500",   pill: "bg-amber-50 text-amber-700 ring-amber-200" },
  planned: { label: "Planned", dot: "bg-slate-400",   pill: "bg-slate-50 text-slate-600 ring-slate-200" },
};

const SITES = [
  {
    key: "civicpulse",
    name: "CivicPulse.blog",
    purpose: "Report and follow civic issues — potholes, water, waste, safety, transit — with verified authority routing.",
    status: "live",
    scope: "India-first · Global",
    url: "https://civicpulse.blog",
    canonical: "civicpulse.blog",
    icon: Landmark,
    tint: "from-emerald-100 to-teal-200",
    accent: "text-emerald-700",
    internalHref: "/civic",
  },
  {
    key: "indias",
    name: "Indias.com",
    purpose: "India-focused editorial and community hub — places, culture, civic and travel.",
    status: "live",
    scope: "India",
    url: "https://indias.com",
    canonical: "indias.com",
    icon: Globe2,
    tint: "from-orange-100 to-amber-200",
    accent: "text-orange-700",
  },
  {
    key: "kerala",
    name: "Kerala.com",
    purpose: "Kerala's community, tourism, health and civic pulse in one place.",
    status: "live",
    scope: "Kerala, India",
    url: "https://kerala.com",
    canonical: "kerala.com",
    icon: MapPin,
    tint: "from-lime-100 to-emerald-200",
    accent: "text-emerald-700",
    internalHref: "/kerala",
  },
  {
    key: "indianfamilytree",
    name: "IndianFamilyTree.com",
    purpose: "Family trees, memorials, heritage and genealogy for the Indian diaspora.",
    status: "live",
    scope: "India · Diaspora",
    url: "https://indianfamilytree.com",
    canonical: "indianfamilytree.com",
    icon: Users,
    tint: "from-rose-100 to-pink-200",
    accent: "text-rose-700",
    internalHref: "/indian-family-tree",
  },
  {
    key: "keralahealth",
    name: "KeralaHealth.com",
    purpose: "Hospitals, clinics, providers, wellness articles and healthcare guidance for Kerala.",
    status: "pilot",
    scope: "Kerala, India",
    url: "https://keralahealth.com",
    canonical: "keralahealth.com",
    icon: HeartPulse,
    tint: "from-red-100 to-rose-200",
    accent: "text-red-700",
    internalHref: "/health",
  },
  {
    key: "websitetech",
    name: "WebsiteTech.pro",
    purpose: "Website tools, tutorials and digital tips for small businesses and creators.",
    status: "pilot",
    scope: "Global",
    url: "https://websitetech.pro",
    canonical: "websitetech.pro",
    icon: Code2,
    tint: "from-indigo-100 to-blue-200",
    accent: "text-indigo-700",
  },
  {
    key: "hotelspulse",
    name: "HotelsPulse.pro",
    purpose: "Hotels, stays and travel accommodations with community-verified reviews.",
    status: "pilot",
    scope: "Global",
    url: "https://hotelspulse.pro",
    canonical: "hotelspulse.pro",
    icon: Hotel,
    tint: "from-sky-100 to-blue-200",
    accent: "text-blue-700",
  },
  {
    key: "photopulse",
    name: "PhotoPulse.pro",
    purpose: "Photography community — share, explore and get discovered.",
    status: "pilot",
    scope: "Global",
    url: "https://photopulse.pro",
    canonical: "photopulse.pro",
    icon: Camera,
    tint: "from-pink-100 to-fuchsia-200",
    accent: "text-fuchsia-700",
  },
  {
    key: "worldpulse",
    name: "WorldPulse.pro",
    purpose: "Destinations, travel stories and global connections across borders.",
    status: "pilot",
    scope: "Global",
    url: "https://worldpulse.pro",
    canonical: "worldpulse.pro",
    icon: Globe2,
    tint: "from-cyan-100 to-sky-200",
    accent: "text-cyan-700",
  },
  {
    key: "usapulse",
    name: "USAPulse.net",
    purpose: "USA-focused pulse for local news, civic, community and places.",
    status: "pilot",
    scope: "USA",
    url: "https://usapulse.net",
    canonical: "usapulse.net",
    icon: Newspaper,
    tint: "from-blue-100 to-indigo-200",
    accent: "text-blue-700",
  },
  {
    key: "familytree",
    name: "FamilyTree.team",
    purpose: "Global family tree platform — collaborative genealogy for everyone.",
    status: "planned",
    scope: "Global",
    url: "https://familytree.team",
    canonical: "familytree.team",
    icon: HomeIcon,
    tint: "from-violet-100 to-purple-200",
    accent: "text-violet-700",
  },
];

const STATUS_KEYS = ["all", "live", "pilot", "planned"];

export default function SocialPulseNetwork() {
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  useEffect(() => {
    document.title = "The SocialPulse Network · Every sister site, one identity";
    setMeta("description", "The SocialPulse Network — every sister site, its purpose, status and canonical URL. CivicPulse.blog, Indias.com, Kerala.com, WorldPulse.pro, HotelsPulse.pro and more.");
    setLink("canonical", "https://socialpulse.com/network");
    setMeta("og:title", "The SocialPulse Network", "property");
    setMeta("og:description", "Every sister site in the SocialPulse network — purpose, status, scope, canonical URL.", "property");
    setMeta("og:type", "website", "property");
    setMeta("og:url", "https://socialpulse.com/network", "property");
  }, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return SITES.filter((s) => {
      if (statusFilter !== "all" && s.status !== statusFilter) return false;
      if (!needle) return true;
      return (
        s.name.toLowerCase().includes(needle) ||
        s.purpose.toLowerCase().includes(needle) ||
        s.scope.toLowerCase().includes(needle) ||
        s.canonical.toLowerCase().includes(needle)
      );
    });
  }, [q, statusFilter]);

  const counts = useMemo(() => ({
    live:    SITES.filter((s) => s.status === "live").length,
    pilot:   SITES.filter((s) => s.status === "pilot").length,
    planned: SITES.filter((s) => s.status === "planned").length,
    total:   SITES.length,
  }), []);

  return (
    <div className="min-h-screen bg-white" data-testid="sp-network-page">
      <SocialPulseHeader />

      {/* Hero */}
      <section
        className="relative overflow-hidden"
        style={{ background: "linear-gradient(180deg, #F0F6FF 0%, #E7F0FA 55%, #F5F9FE 100%)" }}
      >
        <div className="max-w-[1200px] mx-auto px-5 sm:px-6 lg:px-10 pt-10 sm:pt-14 pb-8 sm:pb-12">
          <p className="text-xs font-semibold uppercase tracking-[.22em] text-blue-700">Network Directory</p>
          <h1
            className="mt-3 font-serif font-bold tracking-tight text-slate-900 leading-[1.1] text-3xl sm:text-4xl lg:text-5xl"
            data-testid="sp-network-h1"
          >
            One network. <span className="text-blue-700">{counts.total} pulses.</span>
          </h1>
          <p className="mt-4 sm:mt-5 text-slate-600 text-base sm:text-lg leading-relaxed max-w-2xl">
            Every SocialPulse sister site — its purpose, scope and where it is on
            the launch journey. Sign in on any site and your identity travels
            across the whole network.
          </p>

          <div className="mt-6 flex flex-wrap gap-2">
            <StatChip dot="bg-emerald-500" label={`${counts.live} Live`} />
            <StatChip dot="bg-amber-500"   label={`${counts.pilot} Pilot`} />
            <StatChip dot="bg-slate-400"   label={`${counts.planned} Planned`} />
          </div>
        </div>
      </section>

      {/* Filters */}
      <section className="border-b border-slate-200 bg-white sticky top-16 z-30" data-testid="sp-network-filters">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-6 lg:px-10 py-3 sm:py-4 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
          <label className="relative flex-1 min-w-0">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search the network…"
              data-testid="sp-network-search"
              className="w-full h-10 pl-9 pr-4 rounded-full ring-1 ring-slate-200 focus:ring-2 focus:ring-blue-500 focus:outline-none text-sm text-slate-800 placeholder:text-slate-400 bg-white"
            />
          </label>
          <div className="flex items-center gap-1 overflow-x-auto -mx-1 px-1 shrink-0">
            <span className="hidden sm:inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-slate-500 mr-2">
              <Filter className="w-3.5 h-3.5" /> Status
            </span>
            {STATUS_KEYS.map((k) => (
              <button
                key={k}
                onClick={() => setStatusFilter(k)}
                data-testid={`sp-network-filter-${k}`}
                className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-full text-xs font-semibold whitespace-nowrap transition-colors ${
                  statusFilter === k
                    ? "bg-blue-600 text-white"
                    : "bg-slate-50 text-slate-600 hover:bg-slate-100"
                }`}
              >
                {k !== "all" && (
                  <span className={`w-1.5 h-1.5 rounded-full ${STATUSES[k].dot}`} />
                )}
                {k === "all" ? "All" : STATUSES[k].label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* List */}
      <main className="max-w-[1200px] mx-auto px-5 sm:px-6 lg:px-10 py-8 sm:py-12">
        {filtered.length === 0 && (
          <p className="text-sm text-slate-500 text-center py-14" data-testid="sp-network-empty">
            No sites match your filters.
          </p>
        )}

        <ul className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5" data-testid="sp-network-grid">
          {filtered.map((s) => <SiteRow key={s.key} site={s} />)}
        </ul>

        {/* SSO callout */}
        <div
          className="mt-12 rounded-2xl p-6 sm:p-8 ring-1 ring-violet-200"
          style={{
            background:
              "linear-gradient(135deg, #EEF2FF 0%, #F3E8FF 45%, #ECFDF5 100%)",
          }}
        >
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5">
            <span className="w-11 h-11 grid place-items-center rounded-xl bg-gradient-to-br from-emerald-500 to-violet-600 text-white shrink-0 shadow-sm">
              <Activity className="w-5 h-5" strokeWidth={2.5} />
            </span>
            <div className="flex-1 min-w-0">
              <h2 className="font-serif font-bold text-slate-900 text-xl sm:text-2xl">
                One identity across every site.
              </h2>
              <p className="mt-1.5 text-sm sm:text-base text-slate-700 max-w-2xl">
                Sign in once with SocialPulse SSO and you're recognised
                everywhere in the network — no repeat sign-ups, no repeat
                passwords.
              </p>
            </div>
            <Link
              to="/join"
              data-testid="sp-network-join-cta"
              className="inline-flex items-center gap-2 rounded-full bg-violet-600 hover:bg-violet-700 text-white font-semibold px-5 py-2.5 text-sm transition-colors shrink-0 shadow-sm"
            >
              Join the network <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </main>

      <SocialPulseFooter />
    </div>
  );
}

/* ─────────────────── UI atoms ─────────────────── */

function StatChip({ dot, label }) {
  return (
    <span className="inline-flex items-center gap-2 rounded-full bg-white/70 backdrop-blur ring-1 ring-slate-200 px-3 py-1 text-xs font-semibold text-slate-700">
      <span className={`w-1.5 h-1.5 rounded-full ${dot}`} />
      {label}
    </span>
  );
}

function SiteRow({ site }) {
  const Icon = site.icon;
  const status = STATUSES[site.status];
  return (
    <li
      data-testid={`sp-network-site-${site.key}`}
      className="group relative flex flex-col rounded-2xl ring-1 ring-slate-200 bg-white overflow-hidden hover:ring-blue-300 hover:shadow-md transition-all"
    >
      <div className={`h-1.5 bg-gradient-to-r ${site.tint}`} />
      <div className="p-5 sm:p-6 flex flex-col gap-4 h-full">
        <div className="flex items-start gap-4">
          <span className={`w-11 h-11 grid place-items-center rounded-2xl bg-gradient-to-br ${site.tint} shrink-0`}>
            <Icon className={`w-5 h-5 ${site.accent}`} strokeWidth={2.2} />
          </span>
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className={`font-serif font-bold text-slate-900 text-lg leading-tight ${site.accent}`}>
                {site.name}
              </h3>
              <span
                className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[10.5px] font-semibold uppercase tracking-wider ring-1 ${status.pill}`}
              >
                <span className={`w-1.5 h-1.5 rounded-full ${status.dot}`} />
                {status.label}
              </span>
            </div>
            <p className="mt-1 text-[13px] text-slate-500 flex items-center gap-1.5">
              <MapPin className="w-3 h-3" /> {site.scope}
            </p>
          </div>
        </div>

        <p className="text-sm text-slate-600 leading-relaxed flex-1">
          {site.purpose}
        </p>

        <div className="flex flex-wrap items-center gap-3 pt-2 border-t border-slate-100">
          <a
            href={site.url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`sp-network-url-${site.key}`}
            className="inline-flex items-center gap-1.5 text-sm text-blue-700 hover:text-blue-900 font-semibold min-w-0"
          >
            <span className="truncate">{site.canonical}</span>
            <ExternalLink className="w-3.5 h-3.5 shrink-0" />
          </a>
          <span className="text-slate-300">·</span>
          {site.internalHref ? (
            <Link
              to={site.internalHref}
              data-testid={`sp-network-preview-${site.key}`}
              className="inline-flex items-center gap-1 text-xs font-semibold text-slate-600 hover:text-blue-700"
            >
              Preview inside SocialPulse <ArrowRight className="w-3 h-3" />
            </Link>
          ) : (
            <span className="text-xs text-slate-400 font-medium">External · sister site</span>
          )}
        </div>
      </div>
    </li>
  );
}

function setMeta(name, content, attr = "name") {
  let el = document.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}
function setLink(rel, href) {
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}
